import { FC, useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import RestoreIcon from '@material-ui/icons/Restore';
import { SubmitHandler, FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import Button from 'src/components/Button';
import ConfirmDialog from 'src/components/ConfirmDialog';
import { ConfirmDialogRef } from 'src/components/ConfirmDialog/interfaces';
import DatePicker from 'src/components/Form/DatePicker';
import { IEditPartnerContractParams } from 'src/interfaces/forms/IPartner';
import { IPartnerContract } from 'src/interfaces/models/IPartner';
import PrivateContext from 'src/routes/Private/PrivateContext';
import api from 'src/services/api';
import { handleApiResponseErrors, showFormErrors } from 'src/utils/errors';
import { removeEmpty } from 'src/utils/helpers';
import yupValidate from 'src/utils/yupValidate';
import {
  PartnerContractStoreSchema,
  PartnerContractUpdateSchema,
} from 'src/validators/PartnerContract/save.schema';

import { Container } from './styles';

const Save: FC = () => {
  const { startLayoutLoading, stopLayoutLoading } = useContext(PrivateContext);
  const formRef = useRef<FormHandles>(null);
  const confirmDialogRef = useRef<ConfirmDialogRef>(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const history = useHistory();
  const params = useParams<IEditPartnerContractParams>();

  const yupSchema = params.contractId
    ? PartnerContractUpdateSchema
    : PartnerContractStoreSchema;

  useEffect(() => {
    const loadData = async () => {
      try {
        startLayoutLoading();

        if (params.contractId) {
          const response = await api.get(
            `/beneficiary-admin/partners/${params.id}/contracts/${params.contractId}`,
          );
          const contract = response.data as IPartnerContract;

          setStatus(contract.status);

          formRef.current?.setData(contract);
        }
      } catch (error) {
        handleApiResponseErrors(error.response, 'Erro ao buscar dados.');
      } finally {
        stopLayoutLoading();
      }
    };

    loadData();
  }, [params.id, params.contractId, startLayoutLoading, stopLayoutLoading]);

  const handleOnSubmit: SubmitHandler = async (formData) => {
    try {
      setLoading(true);
      formRef.current?.setErrors({});

      const { success, data, errors } = await yupValidate(yupSchema, formData);

      if (!success) {
        return showFormErrors(errors, formRef);
      }

      if (params.contractId) {
        await api.put(
          `/beneficiary-admin/partners/${params.id}/contracts/${params.contractId}`,
          removeEmpty(data),
        );
      } else {
        await api.post(
          `/beneficiary-admin/partners/${params.id}/contracts`,
          removeEmpty(data),
        );
      }

      toast.success('Dados salvos com sucesso!');
      history.goBack();
    } catch (error) {
      handleApiResponseErrors(error.response, 'Erro', yupSchema, formRef);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);

      if (status === 'active') {
        const isDelete = confirmDialogRef.current?.isDelete();
        let deleteConfig = {};
        if (isDelete) {
          deleteConfig = { data: { delete: true } };
        }

        await api.delete(
          `/beneficiary-admin/partners/${params.id}/contracts/${params.contractId}`,
          deleteConfig,
        );

        toast.success(`Contrato ${isDelete ? 'excluido' : 'desativado'}!`);
      } else {
        await api.post(
          `/beneficiary-admin/partners/${params.id}/contracts/${params.contractId}/restore`,
        );
        toast.success('Contrato restaurado!');
      }
      history.goBack();
    } catch (error) {
      handleApiResponseErrors(error.response, 'Erro ao atualizar contrato.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Form ref={formRef} onSubmit={handleOnSubmit} noValidate>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6">
              {params.contractId ? 'Editar' : 'Novo'} Contrato
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1">
                      Para contratos com prazo indeterminado, deixe a data de
                      expiração em branco.
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <DatePicker
                      name="signed_at"
                      label="Assinado em"
                      disableToolbar={false}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <DatePicker name="notify_at" label="Notificar em" />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <DatePicker name="expire_at" label="Expira em" />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Grid container justify="space-between">
              {status === 'active' ? (
                <Button
                  type="button"
                  loading={loading}
                  startIcon={<CloseIcon />}
                  variant="contained"
                  color="secondary"
                  disabled={!params.contractId}
                  onClick={() => confirmDialogRef.current?.show()}
                >
                  Desativar
                </Button>
              ) : (
                <Button
                  type="button"
                  loading={loading}
                  startIcon={<RestoreIcon />}
                  color="primary"
                  disabled={!params.contractId}
                  onClick={() => confirmDialogRef.current?.show()}
                >
                  Restaurar
                </Button>
              )}

              <Button
                type="submit"
                loading={loading}
                startIcon={<CheckIcon />}
                variant="contained"
                color="primary"
              >
                Salvar
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Form>

      <ConfirmDialog
        ref={confirmDialogRef}
        title={`${status === 'active' ? 'Desativar' : 'Restaurar'} contrato`}
        description="Confirma esta ação?"
        confirmColor={status === 'active' ? 'secondary' : 'primary'}
        onConfirm={handleConfirm}
        canDelete={status === 'active'}
      />
    </Container>
  );
};

export default Save;
